import { useRouter } from 'expo-router';
import { useEffect } from 'react';
import { useReanimatedKeyboardAnimation } from 'react-native-keyboard-controller';
import Reanimated, { useAnimatedStyle } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { OfferedReplyDraft } from './OfferedReplyDraft';
import { useLentComposer } from '../../state/composerBridge';

import { Box } from '@/components/ui/box';

type ReplyDraftScreenProps = {
  conversationId: string | undefined;
};

/**
 * The reply draft on a phone: a full-screen modal over the chat, as the
 * reference assistant is, so the draft has the whole screen and the system
 * bar for its title and Close.
 *
 * It fills the chat's composer through the loan the chat makes while it is
 * mounted beneath. With no loan, such as a link opened cold or a chat that
 * has gone, the draft has nowhere to put its reply, so the screen dismisses
 * itself rather than letting the expert edit a reply they could not use.
 *
 * The keyboard spacer sits under the footer, so the reply's action rises with
 * the keyboard, and stands in for the home indicator when the keyboard is
 * down. The inset comes from the hook because a safe-area view measures a
 * stale inset inside a full-screen modal.
 */
export function ReplyDraftScreen({ conversationId }: ReplyDraftScreenProps) {
  const router = useRouter();

  const composer = useLentComposer(conversationId ?? '');

  const { bottom } = useSafeAreaInsets();

  const { height: keyboardHeight } = useReanimatedKeyboardAnimation();

  const spacerStyle = useAnimatedStyle(() => ({
    height: Math.max(bottom, -keyboardHeight.value),
  }));

  const isMissing = !conversationId || !composer;

  useEffect(() => {
    if (isMissing && router.canDismiss()) {
      router.dismiss();
    }
  }, [isMissing, router]);

  if (isMissing) {
    return null;
  }

  return (
    <Box className="flex-1 bg-background-0">
      <OfferedReplyDraft
        conversationId={conversationId}
        composer={composer}
        onClose={() => router.dismiss()}
        bottomSpace={<Reanimated.View style={spacerStyle} />}
      />
    </Box>
  );
}
